import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DetailScaffold from "./components/DetailScaffold";
import Gallery from "./components/Gallery";
import ProductGridCard from "./components/ProductGridCard";
import ProductPurchasePanel from "./components/ProductPurchasePanel";
import { SpecList } from "./components/DetailBits";
import { ProductBodySkeleton } from "./components/DetailSkeletons";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";
import useDetailOrigin from "./useDetailOrigin";
import useAddToCart from "./useAddToCart";
import { useSupplierProducts } from "./useSupplierBody";
import { fetchProduct } from "./detailService";
import { mapProduct } from "./detailMappers";
import { haversineKm, supplierNotice } from "./deliverability";
import { CART_PATH, supplierPath, supplierProductPath } from "./detailRoutes";

import useCart from "../cart/useCart";
import useSupplierSave from "../favourites/useSupplierSave";
import { getAuthToken } from "../../lib/api/tokenStore";

import "./styles.css";

const HERO_HEIGHT = 320;

const SupplierProductDetailRoute = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { productId: productIdParam, supplierId: supplierIdParam } = useParams();

  const productId = Number(productIdParam);
  const supplierId = Number(supplierIdParam);

  const origin = useDetailOrigin();

  const query = useQuery({
    key: Number.isFinite(productId) ? `product:${productId}` : null,
    tag: QUERY_TAGS.PRODUCT_DETAIL,
    enabled: Number.isFinite(productId),
    fetcher: () => fetchProduct(productId),
  });

  const product = useMemo(() => mapProduct(query.currentData), [query.currentData]);
  const supplier = product?.supplier || null;

  const notice = useMemo(() => {
    if (!supplier) return state?.notice || null;

    const distanceKm =
      Number.isFinite(supplier.distanceKm) && supplier.distanceKm > 0
        ? supplier.distanceKm
        : haversineKm(origin.coordinates, supplier.coordinates);

    return supplierNotice({ ...supplier, distanceKm });
  }, [supplier, state, origin.coordinates]);

  const cart = useCart({ enabled: Boolean(getAuthToken()) });
  const save = useSupplierSave({ enabled: Boolean(getAuthToken()) });
  const { busyAction, addToCart, conflictDialog } = useAddToCart({ cart });

  const [quantity, setQuantity] = useState(1);

  const storeId = supplier?.id ?? (Number.isFinite(supplierId) ? supplierId : null);

  const related = useSupplierProducts({
    supplierId: storeId,
    containerOptionId: product?.containerOption?.id,
    enabled: Boolean(product),
  });

  const relatedProducts = useMemo(
    () => related.products.filter((item) => item.id !== product?.id),
    [related.products, product]
  );

  const cartItem = useMemo(() => {
    if (!product) return null;

    const variableVolume = Boolean(product.containerOption?.variableVolume);
    const materials = product.containerMaterials || [];

    return {
      productId: product.id,
      supplierId: storeId,
      quantity,
      variableVolume,
      volume: variableVolume && product.volume !== null ? product.volume : null,
      containerMaterialId: materials.length === 1 ? materials[0].id : null,
    };
  }, [product, storeId, quantity]);

  const inCart = cartItem ? cart.contains(cartItem) : false;

  const submit = useCallback(
    async (action) => {
      if (!cartItem) return;

      const result = await addToCart({
        action,
        notice,
        supplierName: supplier?.name || state?.supplierName,
        items: [cartItem],
      });

      if (result?.status === "added" && action === "buy") navigate(CART_PATH);
    },
    [cartItem, addToCart, notice, supplier, state, navigate]
  );

  const openRelated = useCallback(
    (item) =>
      navigate(
        supplierProductPath({
          supplierId: storeId,
          productId: item.id,
          coordinates: origin.coordinates,
        }),
        { state: { notice, supplierName: supplier?.name, supplierLogo: supplier?.logoUrl } }
      ),
    [navigate, storeId, origin.coordinates, notice, supplier]
  );

  const specs = useMemo(() => {
    if (!product) return [];

    return [
      { label: "Purchase Type", value: product.containerOption?.defaultTitle },
      { label: "Country Of Origin", value: product.countryOfOrigin?.name },
      { label: "Container Type", value: product.containerType?.defaultTitle },
      {
        label: "Cylinder Type",
        value: (product.containerMaterials || [])
          .map((material) => material.defaultTitle)
          .filter(Boolean)
          .join(", "),
      },
      {
        label: "Cylinder Volume",
        value: product.volume ? `${product.volume} Kg` : "",
      },
      { label: "Fuel Type", value: product.fuelType?.defaultTitle },
      { label: "Brand", value: product.brand?.defaultTitle },
    ];
  }, [product]);

  if (query.isError || (Number.isFinite(productId) === false)) {
    return (
      <div className="sy-detail-error">
        <p>We could not load this product.</p>

        <button
          type="button"
          className="sy-btn sy-btn-primary"
          onClick={() => query.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }

  const supplierName = supplier?.name || state?.supplierName || "Supplier";
  const supplierLogo = supplier?.logoUrl || state?.supplierLogo;
  const savedSupplier = supplier
    ? { id: supplier.id, saved: supplier.saved, name: supplierName }
    : null;

  return (
    <DetailScaffold
      title={product?.name || "Product"}
      heroHeight={HERO_HEIGHT}
      layout="split"
      heroAuto
      hero={
        <Gallery
          images={product?.imageUrls}
          alt={product?.name || "Product"}
          showThumbs
        />
      }
      full={
        product && (
          <>
            <section className="sy-product-info">
              <h2>About this product</h2>

              {product.description ? (
                <p className="sy-product-description">{product.description}</p>
              ) : (
                <p className="sy-product-description sy-muted">
                  The supplier has not added a description for this item yet.
                </p>
              )}

              <SpecList items={specs} />
            </section>

            {relatedProducts.length > 0 && (
              <section className="sy-rail-section">
                <h3>More from {supplierName}</h3>

                <ul className="sy-rail">
                  {relatedProducts.map((item) => (
                    <ProductGridCard
                      key={item.id}
                      product={item}
                      onOpen={openRelated}
                    />
                  ))}
                </ul>
              </section>
            )}
          </>
        )
      }
    >
      {!product ? (
        <ProductBodySkeleton />
      ) : (
        <ProductPurchasePanel
          product={product}
          supplier={supplier}
          supplierName={supplierName}
          supplierLogo={supplierLogo}
          notice={notice}
          quantity={quantity}
          inCart={inCart}
          busy={cart.isSaving}
          busyAction={busyAction}
          canSave={save.canSave && Boolean(savedSupplier)}
          saved={savedSupplier ? save.isSaved(savedSupplier) : false}
          savePending={savedSupplier ? save.isPending(savedSupplier) : false}
          onQuantityChange={setQuantity}
          onToggleSave={() => savedSupplier && save.toggleSaved(savedSupplier)}
          onAdd={() => submit("add")}
          onBuy={() => submit("buy")}
          onViewCart={() => navigate(CART_PATH)}
          onVisitStore={
            storeId
              ? () =>
                  navigate(
                    supplierPath({
                      supplierId: storeId,
                      distanceKm: supplier?.distanceKm,
                      coordinates: origin.coordinates,
                    })
                  )
              : null
          }
        />
      )}

      {conflictDialog}
    </DetailScaffold>
  );
};

export default SupplierProductDetailRoute;
